/**
 * Pilot state: hypoxia and wounds. Note what is deliberately *absent* —
 * g-induced blackout. No manual, review or forum source documents one in the
 * 1990 game; the blackout the manual describes is altitude/oxygen only
 * (docs/game/flight-model.md §5), verbatim:
 *
 * > "The pilots of WWI didn't have oxygen masks, and consequently a pilot who
 * > flew above 20,000 feet for too long would get dizzy and occasionally even
 * > blackout from lack of oxygen. Hopefully, when the pilot's aircraft went
 * > into a dive, the pilot would wake up before hitting the ground."
 *
 * That last sentence is the design: the meter drains on descent, so waking up
 * before the ground is an emergent race, not a scripted event. The g-load
 * consequence Red Baron models is structural instead — see damage.ts.
 */

import type { Tunables } from "./tunables.ts";
import { clamp } from "./units.ts";

export const PILOT_CONDITIONS = ["normal", "dizzy", "blackout"] as const;

export type PilotCondition = (typeof PILOT_CONDITIONS)[number];

export interface PilotState {
  /** 0..1 hidden oxygen-debt meter. */
  readonly hypoxia: number;
  readonly condition: PilotCondition;
  readonly wounded: boolean;
  /** Seconds left before bleeding out; −1 when not wounded. Landing is what stops it. */
  readonly bleedOutSeconds: number;
  readonly alive: boolean;
  /**
   * The seat is empty because he went over the side (AD-021, a **declared port
   * enhancement** — the original has no bail-out and WWI Allied pilots carried
   * no parachutes; see `docs/engineering/bail-out-design.md`).
   *
   * Distinct from `alive`, and the distinction is the whole feature: a bailed
   * pilot is *alive* and no longer in the aeroplane, where a dead one is in it
   * and not alive. `alive` therefore stays true, and everything that asks
   * "is anybody flying this?" asks `derelict.ts`'s `isWreck` instead — which is
   * the one helper this concept is spelled in.
   *
   * What happens to him from here is **not** `SimState`'s business: the canopy,
   * the drift and where he comes down live on `EncounterState.parachute`
   * (`combat/parachute.ts`), because a man under silk is not an aeroplane.
   */
  readonly bailedOut: boolean;
}

export interface PilotUpdate {
  readonly hypoxiaEnabled: boolean;
  readonly altitudeFt: number;
  readonly tunables: Tunables;
  readonly dt: number;
}

export const HEALTHY_PILOT: PilotState = {
  hypoxia: 0,
  condition: "normal",
  wounded: false,
  bleedOutSeconds: -1,
  alive: true,
  bailedOut: false,
};

function nextCondition(previous: PilotCondition, meter: number, tunables: Tunables): PilotCondition {
  if (meter >= tunables.hypoxiaBlackoutThreshold) return "blackout";
  if (previous === "blackout") {
    return meter > tunables.hypoxiaWakeThreshold ? "blackout" : "dizzy";
  }
  return meter >= tunables.hypoxiaDizzyThreshold ? "dizzy" : "normal";
}

export function updatePilot(state: PilotState, update: PilotUpdate): PilotState {
  const { tunables } = update;
  const excessFt = update.altitudeFt - tunables.hypoxiaAltitudeFt;
  const meter = update.hypoxiaEnabled
    ? clamp(
        state.hypoxia +
          (excessFt > 0
            ? (excessFt * update.dt) / tunables.hypoxiaFootSecondsToBlackout
            : -tunables.hypoxiaRecoveryPerSecond * update.dt),
        0,
        1.5,
      )
    : 0;

  const bleeding = state.wounded && state.bleedOutSeconds > 0;
  const bleedOutSeconds = bleeding ? Math.max(0, state.bleedOutSeconds - update.dt) : state.bleedOutSeconds;

  return {
    hypoxia: meter,
    condition: update.hypoxiaEnabled ? nextCondition(state.condition, meter, tunables) : "normal",
    wounded: state.wounded,
    bleedOutSeconds,
    alive: state.alive && !(bleeding && bleedOutSeconds <= 0),
    // Carried, never set here: leaving the aeroplane is a command
    // (`combat/parachute.ts`), not something the physiology tick can decide.
    bailedOut: state.bailedOut,
  };
}

/**
 * Hook for the combat lane: a hit on the pilot starts the bleed-out clock the
 * manual's career mode hangs on ("possibly bleeding to death if wounded and
 * unable to land at an aerodrome in time"). Re-wounding restarts the clock at
 * the shorter of the two remaining times.
 */
export function woundPilot(state: PilotState, tunables: Tunables): PilotState {
  const fresh = tunables.woundBleedOutSeconds;
  const remaining = state.wounded && state.bleedOutSeconds >= 0 ? Math.min(state.bleedOutSeconds, fresh) : fresh;
  return { ...state, wounded: true, bleedOutSeconds: remaining };
}

/** Landing (or any medical outcome the campaign lane models) stops the clock. */
export function stopBleeding(state: PilotState): PilotState {
  return state.wounded ? { ...state, bleedOutSeconds: -1 } : state;
}

/**
 * A blacked-out pilot is not flying the aircraft — nor is a dead one, nor one
 * who has gone over the side (AD-021).
 *
 * `bailedOut` belongs here rather than only in `isWreck` because this predicate
 * has a second consumer that `isWreck` does not: `frame-signals.ts` asks it
 * whether the trigger can fire. A man under a canopy is not shooting, and
 * spelling that a second way locally is exactly the fork `lessons.md`'s
 * doomed-descent entry was written about.
 */
export function controlsLocked(state: PilotState): boolean {
  return !state.alive || state.bailedOut || state.condition === "blackout";
}
